import ChartDataWrapper from '@unctad-infovis/general-tools/components/ChartDataWrapper.jsx';
import { useEffect, useState } from 'react';

import parseTsv from '../helpers/ParseTsv.js';
import resolveDatawrapperCsvUrl from '../helpers/ResolveDatawrapperCsvUrl.js';
import TabSwitcher from './TabSwitcher.jsx';
import './GlobalTradeSnapshot.css';

// unctad.org already loads gtag.js site-wide, so this widget only needs to call into it, not
// load its own copy.
const analytics = window.gtag || undefined;

const GlobalTradeSnapshot = ({ meta }) => {
  const [activeIndex, setActiveIndex] = useState(0);
  // 'tabs' (default) shows one chart at a time behind a tab switcher; 'grid' shows all
  // charts side by side (stacking to one column on narrow screens) with no tab switcher.
  // Both stay wired up so switching is a one-line meta.json change, not a rewrite.
  const layout = meta.layout === 'grid' ? 'grid' : 'tabs';
  // The download link's target lives in a row of the same Datawrapper table the Stats strip
  // already reads (rather than a build-time URL), so a new release only needs that table
  // updated — no code or meta.json change, matching the CSV-driven pattern used there.
  const [downloadUrl, setDownloadUrl] = useState(null);

  useEffect(() => {
    if (!meta.download?.datawrapper_chart_id) return;

    resolveDatawrapperCsvUrl(meta.download.datawrapper_chart_id)
      .then(csvUrl => fetch(csvUrl))
      .then(response => response.text())
      .then(text => {
        const rows = parseTsv(text);
        const row = rows.find(r => r[meta.download.match.column] === meta.download.match.value);
        setDownloadUrl(row?.[meta.download.value_column] || null);
      })
      .catch(error => console.error(error));
  }, [meta.download]);

  // meta.download.label is a substring of meta.description (e.g. "Facts and Figures edition
  // of the Global Trade Update") that becomes the link itself, rather than a separate
  // trailing sentence — split once here so the render stays a plain [before, after] insert.
  const descriptionParts = meta.download && meta.description?.includes(meta.download.label) ? meta.description.split(meta.download.label) : null;

  // People rarely click past the first tab, so this tracks which ones actually get used.
  const changeTab = index => {
    setActiveIndex(index);

    if (typeof analytics !== 'undefined') {
      analytics('event', 'Tab Click', {
        event_category: __PROJECT_NAME__,
        event_label: meta.tabs[index].label,
        transport_type: 'beacon'
      });
    }
  };

  return (
    <section className="global_trade_snapshot global-trade-snapshot">
      <div className="global_trade_snapshot_content">
        <div className="global_trade_snapshot_header">
          <div className="global_trade_snapshot_heading">
            <h2 className="global_trade_snapshot_title">{meta.title}</h2>
            {meta.description && (
              <p className="global_trade_snapshot_description">
                {descriptionParts && downloadUrl ? (
                  <>
                    {descriptionParts[0]}
                    <a className="global_trade_snapshot_download" href={downloadUrl} rel="noreferrer" target="_blank">
                      {meta.download.label}
                    </a>
                    {descriptionParts[1]}
                  </>
                ) : (
                  meta.description
                )}
              </p>
            )}
          </div>
          {layout === 'tabs' && <TabSwitcher activeIndex={activeIndex} onChange={changeTab} tabs={meta.tabs} />}
        </div>
        <div className={`global_trade_snapshot_charts global_trade_snapshot_charts--${layout}`}>
          {meta.tabs.map((tab, index) => (
            // In 'tabs' layout, all tabs mount once and stay mounted — toggling `hidden` instead
            // of conditionally rendering avoids unmount/remount on every switch, which briefly
            // collapsed this area's height and made the browser clamp scroll position back up
            // the page. In 'grid' layout nothing is ever hidden, so this is always false there.
            // ChartDataWrapper only starts loading once its own container is visible, so a
            // hidden tab's chart doesn't load until it's actually selected.
            // Keyed on label, not chart_id — several tabs can share the same chart_id (e.g.
            // while real per-tab charts are pending) and chart_id must stay unique as a key.
            <div className="global_trade_snapshot_chart" hidden={layout === 'tabs' && index !== activeIndex} key={tab.label}>
              {layout === 'grid' && <p className="global_trade_snapshot_chart_label">{tab.label}</p>}
              <ChartDataWrapper chart_id={tab.chart_id} />
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default GlobalTradeSnapshot;
